import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';

const fmt = (n) => (n || 0).toLocaleString('en-IN');

const statusStyle = (status) => {
  if (status === 'Success') return { bg: 'rgba(34,197,94,0.12)', color: '#22c55e', icon: 'check_circle' };
  if (status === 'Failed')  return { bg: 'rgba(239,68,68,0.12)',  color: '#f87171',  icon: 'cancel' };
  return                          { bg: 'rgba(234,179,8,0.12)',   color: '#eab308',  icon: 'pending' };
};

const VendorWallet = () => {
  const { role } = useAuth();
  const [data, setData]         = useState(null);
  const [vendor, setVendor]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [amount, setAmount]     = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast]       = useState(null);
  
  const [showBankModal, setShowBankModal] = useState(false);
  const [bankForm, setBankForm] = useState({ bankName: '', accountNumber: '', ifscCode: '', accountHolder: '', pan: '' });
  const [updatingBank, setUpdatingBank] = useState(false);
  const [showBankAcc, setShowBankAcc] = useState(false);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchData = useCallback(async () => {
    try {
      const isAcarya = role === 'ACARYA';
      const [walletRes, profileRes] = await Promise.all([
        api.get('/wallet/summary'),
        isAcarya ? api.get('/acaryas/me/profile') : api.get('/vendors/me')
      ]);
      setData(walletRes.data.data);
      if (profileRes.data.success) {
        setVendor(profileRes.data.data);
      }
    } catch (err) {
      console.error('Wallet fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [role]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePayoutRequest = async (e) => {
    e.preventDefault();
    const num = Number(amount);
    if (!num || num < 1) return showToast('Please enter a valid amount', 'error');
    if (num > (data?.availableBalance || 0)) return showToast('Amount exceeds available balance', 'error');

    setSubmitting(true);
    try {
      await api.post('/wallet/payout-request', { amount: num });
      showToast(`Payout request of ₹${fmt(num)} submitted successfully!`);
      setAmount('');
      fetchData();
    } catch (err) {
      showToast(err?.response?.data?.message || 'Failed to submit request', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateBank = async (e) => {
    e.preventDefault();
    setUpdatingBank(true);
    try {
      const isAcarya = role === 'ACARYA';
      if (isAcarya) {
        const res = await api.put('/acaryas/me/payout-info', {
          payoutInfo: {
            bankAccount: bankForm.accountNumber,
            ifsc: bankForm.ifscCode,
            pan: bankForm.pan
          }
        });
        if (res.data.success) {
          showToast('Bank details updated successfully!');
          setVendor(prev => ({ ...prev, payoutInfo: res.data.data }));
          setShowBankModal(false);
        }
      } else {
        const res = await api.put('/vendors/me/payout-info', { bank: bankForm });
        if (res.data.success) {
          showToast('Bank details updated successfully!');
          setVendor(res.data.data);
          setShowBankModal(false);
        }
      }
    } catch (err) {
      console.error(err);
      showToast('Failed to update bank details', 'error');
    } finally {
      setUpdatingBank(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0e0404' }}>
        <div className="text-center">
          <div className="w-16 h-16 rounded-full border-4 border-[#E8B84B]/20 border-t-[#E8B84B] animate-spin mx-auto mb-6" />
          <p className="text-[#E8B84B] font-serif text-lg tracking-widest">LOADING WALLET...</p>
        </div>
      </div>
    );
  }

  const available = data?.availableBalance ?? 0;
  const delivered = data?.deliveredRevenue ?? 0;
  const pending   = data?.pendingAmount ?? 0;
  const paid      = data?.successAmount ?? 0;
  const payouts   = data?.payouts ?? [];

  return (
    <div className="min-h-screen pb-16" style={{ background: 'linear-gradient(160deg, #0e0404 0%, #1a0808 40%, #120606 100%)' }}>
      {/* Toast */}
      {toast && (
        <div className="fixed top-6 right-6 z-50 px-5 py-3 rounded-xl font-bold text-sm shadow-xl transition-all"
          style={{ background: toast.type === 'error' ? '#f87171' : '#22c55e', color: '#fff' }}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="relative overflow-hidden px-8 pt-10 pb-8">
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse 60% 80% at 80% 50%, rgba(212,175,55,0.06) 0%, transparent 70%)' }} />
        <div className="absolute right-8 top-4 material-symbols-outlined text-[120px] select-none pointer-events-none"
          style={{ color: 'rgba(212,175,55,0.05)', lineHeight: 1 }}>account_balance_wallet</div>
        <h1 className="relative z-10 font-serif text-3xl font-bold mb-1" style={{ color: '#FDF6E3' }}>My Wallet</h1>
        <div className="relative z-10 flex flex-wrap gap-4 justify-between items-center mt-2">
          <p className="text-xs font-bold uppercase tracking-[0.3em]" style={{ color: 'rgba(212,175,55,0.6)' }}>
            Earnings · Payouts · Ledger
          </p>
          <button 
            onClick={() => {
              const isAcarya = role === 'ACARYA';
              if (isAcarya) {
                setBankForm({
                  bankName: '',
                  accountNumber: vendor?.payoutInfo?.bankAccount || '',
                  ifscCode: vendor?.payoutInfo?.ifsc || '',
                  accountHolder: '',
                  pan: vendor?.payoutInfo?.pan || ''
                });
              } else {
                setBankForm({
                  bankName: vendor?.bank?.bankName || '',
                  accountNumber: vendor?.bank?.accountNumber || '',
                  ifscCode: vendor?.bank?.ifscCode || '',
                  accountHolder: vendor?.bank?.accountHolder || '',
                  pan: vendor?.panNumber || ''
                });
              }
              setShowBankModal(true);
            }}
            className="px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all hover:bg-[#E8B84B] hover:text-black"
            style={{ border: '1px solid rgba(212,175,55,0.3)', color: '#E8B84B', background: 'rgba(212,175,55,0.1)' }}
          >
            Update Payout Settings
          </button>
        </div>
      </div>

      <div className="px-8 space-y-8">

        {/* Balance Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Available Balance — Hero */}
          <div className="lg:col-span-2 rounded-3xl p-6 relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, rgba(212,175,55,0.15) 0%, rgba(212,175,55,0.05) 100%)', border: '1px solid rgba(212,175,55,0.3)' }}>
            <div className="absolute -bottom-6 -right-6 w-32 h-32 rounded-full opacity-20"
              style={{ background: '#E8B84B', filter: 'blur(40px)' }} />
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 rounded-full bg-[#E8B84B] animate-pulse" />
              <span className="text-[9px] font-black uppercase tracking-[0.3em]" style={{ color: '#E8B84B' }}>Available Balance</span>
            </div>
            <div className="font-serif text-5xl font-black leading-none mb-2" style={{ color: '#FDF6E3' }}>
              ₹{fmt(available)}
            </div>
            <p className="text-xs" style={{ color: 'rgba(212,175,55,0.5)' }}>Ready to request payout</p>
          </div>

          {/* Total Earned */}
          <div className="rounded-3xl p-5 relative overflow-hidden"
            style={{ background: 'rgba(34,197,94,0.04)', border: '1px solid rgba(34,197,94,0.15)' }}>
            <span className="material-symbols-outlined text-[20px] mb-3 block" style={{ color: '#22c55e' }}>payments</span>
            <div className="font-bold text-2xl" style={{ color: '#FDF6E3' }}>₹{fmt(delivered)}</div>
            <div className="text-[9px] font-black uppercase tracking-widest mt-1" style={{ color: 'rgba(34,197,94,0.6)' }}>Total Earned</div>
            <div className="text-[10px] mt-1" style={{ color: 'rgba(255,255,255,0.3)' }}>Delivered orders</div>
          </div>

          {/* In Transit / Paid Out */}
          <div className="rounded-3xl p-5 space-y-4" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div>
              <div className="text-[9px] font-black uppercase tracking-widest mb-1" style={{ color: 'rgba(234,179,8,0.6)' }}>In Transit</div>
              <div className="font-bold text-lg" style={{ color: '#FDF6E3' }}>₹{fmt(pending)}</div>
            </div>
            <div className="h-px bg-white/5" />
            <div>
              <div className="text-[9px] font-black uppercase tracking-widest mb-1" style={{ color: 'rgba(34,197,94,0.6)' }}>Paid Out</div>
              <div className="font-bold text-lg" style={{ color: '#FDF6E3' }}>₹{fmt(paid)}</div>
            </div>
          </div>
        </div>

        {/* Payout Request Form */}
        <div className="rounded-3xl p-7 relative overflow-hidden"
          style={{ background: 'rgba(212,175,55,0.03)', border: '1px solid rgba(212,175,55,0.2)' }}>
          <h2 className="font-serif text-xl font-bold mb-1" style={{ color: '#FDF6E3' }}>Request Payout</h2>
          <p className="text-xs mb-5" style={{ color: 'rgba(212,175,55,0.5)' }}>
            Enter the amount you want to withdraw. Max: ₹{fmt(available)}
          </p>

          <form onSubmit={handlePayoutRequest} className="flex gap-4 flex-wrap items-end">
            <div className="flex-1" style={{ minWidth: 220 }}>
              <label className="text-[10px] font-black uppercase tracking-widest mb-2 block" style={{ color: 'rgba(212,175,55,0.6)' }}>
                Amount (INR)
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg font-bold" style={{ color: '#E8B84B' }}>₹</span>
                <input
                  type="number"
                  min="1"
                  max={available}
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                  placeholder="Enter amount"
                  className="w-full pl-8 pr-4 py-3.5 rounded-xl text-white font-bold text-sm outline-none"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(212,175,55,0.25)' }}
                />
              </div>
            </div>

            <button
              type="button"
              onClick={() => setAmount(String(Math.floor(available)))}
              className="px-4 py-3.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all"
              style={{ background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.2)', color: '#E8B84B' }}
            >
              Max
            </button>

            <button
              type="submit"
              disabled={submitting || available <= 0}
              className="px-8 py-3.5 rounded-xl font-bold text-sm uppercase tracking-widest transition-all flex items-center gap-2"
              style={{
                background: submitting || available <= 0 ? 'rgba(212,175,55,0.3)' : '#E8B84B',
                // At 30% gold the disabled fill is dark, so near-black ink
                // vanished into it — the disabled state keeps parchment.
                color: submitting || available <= 0 ? '#C9BDA6' : '#1a0808',
                cursor: submitting || available <= 0 ? 'not-allowed' : 'pointer'
              }}
            >
              <span className="material-symbols-outlined text-[18px]">send</span>
              {submitting ? 'Submitting...' : 'Request Payout'}
            </button>
          </form>

          {available <= 0 && (
            <p className="text-xs mt-4" style={{ color: '#f87171' }}>
              No available balance. {role === 'ACARYA' ? 'Consultations must be completed' : 'Orders must be marked as Delivered'} before you can request a payout.
            </p>
          )}
        </div>

        {/* Payout History */}
        <div className="rounded-3xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(212,175,55,0.12)' }}>
          <div className="p-6 border-b" style={{ borderColor: 'rgba(212,175,55,0.1)' }}>
            <h2 className="font-serif text-xl font-bold" style={{ color: '#FDF6E3' }}>Payout History</h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr style={{ background: 'rgba(212,175,55,0.05)' }}>
                  <th className="px-6 py-4 text-[10px] uppercase font-black tracking-widest text-[#E8B84B]/70">Request ID</th>
                  <th className="px-6 py-4 text-[10px] uppercase font-black tracking-widest text-[#E8B84B]/70">Date</th>
                  <th className="px-6 py-4 text-[10px] uppercase font-black tracking-widest text-[#E8B84B]/70">Status</th>
                  <th className="px-6 py-4 text-[10px] uppercase font-black tracking-widest text-[#E8B84B]/70">Admin Note</th>
                  <th className="px-6 py-4 text-[10px] uppercase font-black tracking-widest text-[#E8B84B]/70 text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {payouts.length > 0 ? payouts.map(p => {
                  const s = statusStyle(p.status);
                  return (
                    <tr key={p._id} className="hover:bg-white/[0.02] transition-colors">
                      <td className="px-6 py-5">
                        <span className="font-mono text-xs text-white/40 bg-white/5 px-2 py-1 rounded">
                          {String(p._id).slice(-8).toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-5">
                        <div className="text-sm font-bold text-white/80">
                          {new Date(p.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </div>
                        <div className="text-[10px] text-white/30 mt-0.5">
                          {new Date(p.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <span className="flex items-center gap-1.5 w-fit px-3 py-1 rounded-md text-[10px] font-black uppercase tracking-wider"
                          style={{ background: s.bg, color: s.color }}>
                          <span className="material-symbols-outlined text-[12px]">{s.icon}</span>
                          {p.status}
                        </span>
                      </td>
                      <td className="px-6 py-5 text-sm text-white/40 italic">{p.note || '—'}</td>
                      <td className="px-6 py-5 text-right">
                        <span className="font-bold text-lg" style={{ color: '#FDF6E3' }}>₹{fmt(p.amount)}</span>
                      </td>
                    </tr>
                  );
                }) : (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center">
                      <span className="material-symbols-outlined text-5xl block mb-3" style={{ color: 'rgba(255,255,255,0.1)' }}>receipt_long</span>
                      <p className="text-white/30 font-bold">No payout requests yet</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showBankModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="bg-[#0E0E26] border border-white/10 rounded-2xl p-6 w-full max-w-md shadow-2xl relative">
            <h2 className="text-xl font-bold text-white mb-4 font-serif">Update Bank Details</h2>
            <p className="text-sm text-stone-400 mb-6">Ensure your banking credentials are accurate to receive payouts.</p>
            <form onSubmit={handleUpdateBank} className="space-y-4">
              {role !== 'ACARYA' && (
                <>
                  <div>
                    <label className="text-[10px] text-white/30 uppercase font-black tracking-wider block mb-1">Bank Name</label>
                    <input 
                      type="text" 
                      value={bankForm.bankName} 
                      onChange={(e) => setBankForm({...bankForm, bankName: e.target.value})}
                      className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#E8B84B] transition-colors"
                      placeholder="e.g. State Bank of India"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-white/30 uppercase font-black tracking-wider block mb-1">Account Holder Name</label>
                    <input 
                      type="text" 
                      value={bankForm.accountHolder} 
                      onChange={(e) => setBankForm({...bankForm, accountHolder: e.target.value})}
                      className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#E8B84B] transition-colors"
                      placeholder="As per bank records"
                      required
                    />
                  </div>
                </>
              )}
              <div>
                <label className="text-[10px] text-white/30 uppercase font-black tracking-wider block mb-1">IFSC Code</label>
                <input 
                  type="text" 
                  value={bankForm.ifscCode} 
                  onChange={(e) => setBankForm({...bankForm, ifscCode: e.target.value.toUpperCase()})}
                  className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#E8B84B] transition-colors"
                  placeholder="e.g. SBIN0001234"
                  required
                />
              </div>
              <div>
                <label className="text-[10px] text-white/30 uppercase font-black tracking-wider block mb-1">Account Number</label>
                <div className="relative">
                  <input 
                    type={showBankAcc ? "text" : "password"} 
                    value={bankForm.accountNumber} 
                    onChange={(e) => setBankForm({...bankForm, accountNumber: e.target.value})}
                    className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-2.5 pr-10 text-sm text-white focus:outline-none focus:border-[#E8B84B] transition-colors font-mono"
                    placeholder="Account Number"
                    required
                  />
                  <button 
                    type="button"
                    onClick={() => setShowBankAcc(!showBankAcc)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white bg-transparent border-none p-0 outline-none flex items-center justify-center"
                  >
                    <span className="material-symbols-outlined text-[18px]">
                      {showBankAcc ? 'visibility_off' : 'visibility'}
                    </span>
                  </button>
                </div>
              </div>
              <div>
                <label className="text-[10px] text-white/30 uppercase font-black tracking-wider block mb-1">PAN Number</label>
                <input 
                  type="text" 
                  value={bankForm.pan} 
                  onChange={(e) => setBankForm({...bankForm, pan: e.target.value.toUpperCase()})}
                  className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#E8B84B] transition-colors"
                  placeholder="e.g. ABCDE1234F"
                  required
                />
              </div>
              <div className="flex gap-3 pt-4 border-t border-white/10 mt-6">
                <button 
                  type="button" 
                  onClick={() => setShowBankModal(false)}
                  className="flex-1 py-2.5 rounded-xl border border-white/10 text-white/70 hover:bg-white/5 font-bold text-sm transition-all"
                  disabled={updatingBank}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="flex-1 py-2.5 rounded-xl bg-[#E8B84B] text-black font-bold text-sm hover:bg-[#b5952f] transition-all"
                  disabled={updatingBank}
                >
                  {updatingBank ? 'Updating...' : 'Save Details'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default VendorWallet;
